import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ImportTemplateResult,
  TemplateEntityLevel,
} from '../../models/template-transfer';

/**
 * Thin client for the template import/export endpoints (US 351886).
 * Auth (Bearer) and customerId headers are added by the global AuthInterceptor,
 * so this just builds the request. Hand-maintained outside the generated
 * `src/app/api` folder; routes match ExportTemplateController / ImportTemplateController.
 */
@Injectable({ providedIn: 'root' })
export class TemplateTransferService {
  private readonly baseUrl = `${environment.apiBaseUrl}/api/v1/Template`;

  constructor(private http: HttpClient) {}

  /**
   * GET the exported template (self-contained gzip) for a component by its DB primary key.
   * NOTE: `id` must be the primary key, not a RefId. On error the body is a Blob
   * (responseType 'blob') — use extractBlobErrorMessage() to read it.
   */
  exportTemplate(level: TemplateEntityLevel, id: number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/Export/${level}/${id}`, {
      responseType: 'blob',
    });
  }

  /**
   * Export a live (version-bound) segment by its RefId within a workflow. The Studio surfaces
   * RefIds (not primary keys) for version-bound components, so live exports address by RefId.
   */
  exportSegmentByRef(
    workflowId: number,
    segmentRefId: number
  ): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/Export/Segment/${workflowId}/${segmentRefId}`,
      { responseType: 'blob' }
    );
  }

  /** Export a live step by its RefId path within a workflow. */
  exportStepByRef(
    workflowId: number,
    segmentRefId: number,
    stepRefId: number
  ): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/Export/Step/${workflowId}/${segmentRefId}/${stepRefId}`,
      { responseType: 'blob' }
    );
  }

  /** Export a live action by its RefId path within a workflow. */
  exportActionByRef(
    workflowId: number,
    segmentRefId: number,
    stepRefId: number,
    actionRefId: number
  ): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/Export/Action/${workflowId}/${segmentRefId}/${stepRefId}/${actionRefId}`,
      { responseType: 'blob' }
    );
  }

  /** POST a previously exported file as a new customer-specific template at the given level. */
  importTemplate(
    level: TemplateEntityLevel,
    file: File
  ): Observable<ImportTemplateResult> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ImportTemplateResult>(
      `${this.baseUrl}/Import/${level}`,
      formData
    );
  }
}
